/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.13.0
 */

import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging';

import { icons } from '../../../../../assets';
import { appHistoryPush } from '../../../../../modules/app/appHistory';
import { EOnboardingComponentsId } from '../../../../../modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '../../../../../modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '../../../../../modules/role/selectors/guardSelector';
import { wizardStorageClearStepsAction } from '../../../../../modules/wizard/wizardActions';
import { EWizardName } from '../../../../../modules/wizard/wizardConstants';
import { expensesAnalyzerRedirectCallsDetail } from '../../../../../pages/PageExpensesAnalyzer/pageExpensesAnalyzerActions';
import { ExternalRoutePath, openExternalRoute } from '../../../../../routes/constants/externalRoutes';
import { IRouteParams, RoutePath, SUBSCRIBER_ID } from '../../../../../routes/constants/routesConstants';
import { stringFormat } from '../../../../../utils/stringFormat';
import { TableFooterNameType } from '../../../../Table/types/tableTypes';
import {
    TTableButtonsGetTranslation,
    TTableButtonsLogBusinessEvent,
    TUseTableFooterButtonsOnboarding,
    TUseTableFooterButtonsUrl,
} from '../useTableFooterButtons';

const useTableFooterButtonCallDetails = (
    getTranslation: TTableButtonsGetTranslation,
    logBusinessEvent: TTableButtonsLogBusinessEvent,
    tableFooterName: TableFooterNameType,
) => {
    const dispatch = useDispatch();
    const { clientId } = useParams<IRouteParams>();

    const isCallDetailsEnabled = useSelector(
        makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageWizardCallsDetail),
    );

    return {
        className: 'table-footer__link link-statistic',
        dataName: 'statistic',
        icon: icons.statisticsIcon,
        id: AccessComponentKeyEnum.FooterButtonCallDetails,
        ...mapTableFooterNameToOnboarding[tableFooterName],
        ...getTranslation(AccessComponentKeyEnum.FooterButtonCallDetails),

        onClick: useCallback(() => {
            logBusinessEvent(ELogBusinessEventName.USER_CALL_DETAILS_MASTER_TO_STEP_1, ELogBusinessEventType.common);
            dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.CALLS_DETAIL }));
            if (tableFooterName === TableFooterNameType.expensesAnalyzer) {
                dispatch(expensesAnalyzerRedirectCallsDetail());
                return;
            }
            const urlConfig = mapTableFooterNameToUrl[tableFooterName];
            if (!urlConfig) return;

            isCallDetailsEnabled
                ? appHistoryPush(urlConfig.url.replace(SUBSCRIBER_ID, clientId))
                : openExternalRoute(stringFormat(urlConfig.external, clientId));
        }, [logBusinessEvent, dispatch, tableFooterName, isCallDetailsEnabled, clientId]),
    };
};

const mapTableFooterNameToUrl: Partial<TUseTableFooterButtonsUrl> = {
    [TableFooterNameType.subscriberId]: {
        url: RoutePath.callsDetailMobileInfo,
        external: ExternalRoutePath.mobileDetailsId,
    },
    [TableFooterNameType.subscriberMobile]: {
        url: RoutePath.callsDetailMobile,
        external: ExternalRoutePath.mobileDetails,
    },
};

const mapTableFooterNameToOnboarding: TUseTableFooterButtonsOnboarding = {
    [TableFooterNameType.subscriberMobile]: {
        onboarding: EOnboardingComponentsId.subscriberTableFooterButtonCallDetails,
    },
    [TableFooterNameType.expensesAnalyzer]: {
        onboarding: EOnboardingComponentsId.expensesAnalyzerAccountTableFooterButtonCallDetails,
    },
};

export default useTableFooterButtonCallDetails;
